<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as NewRFCDialogFormInitialData,
  ValidData as NewRFCDialogFormValidData,
} from '@/components/dialogForms/NewRFCDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import ReportGenerateDialogAlert from '@/components/alerts/ReportGenerateDialogAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import NewRFCDialogForm from '@/components/dialogForms/NewRFCDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  jobId?: number;
  jobDate?: string;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  jobId: undefined,
  jobDate: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: NewRFCDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: NewRFCDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<NewRFCDialogFormInitialData>(() => {
  return {
    subject: `Update to job #${props.jobId || ''}`,
    message: undefined,
    attachments: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper :class="$style.newRFCDialog" data-testid="newRFCDialog">
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        New request for change
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <NewRFCDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
      <ReportGenerateDialogAlert
        icon="mdi-alert-circle-outline"
        :class="$style.alert"
      >
        The notification will be sent to our booking team
      </ReportGenerateDialogAlert>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-plus"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Create
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.newRFCDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .alert {
    margin-top: 15px;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
